import React, { useState, useEffect } from 'react';

import { Box } from '@chakra-ui/react';
import { getDeerPulseKickstartRoute } from 'Deer/constants/nonDeerRoutes';
import { useHistory, useRouteMatch, withRouter } from 'react-router-dom';
import CohortSidepanel from 'screens/common/CohortCreation/CohortSidePanel';
import { CohortCreationContextProvider } from 'screens/common/CohortCreation/CohortSidePanelContext';
import { CohortCreationSidePanelControlProvider } from 'screens/common/CohortCreation/CohortSidePanelControlContext';
import styled, { css } from 'styled-components';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { PULSE_KICKSTART_STEPS } from 'constants/ProjectConstants';
import { goToPulseSettingPage, goToPulseKickstart } from 'constants/helper';

import { getSurvey } from 'services/engagement';

import PulseKickstartSteps from './PulseKickstartSteps';
import Reminders from './Reminders';
import Settings from './Settings';
import Summary from './Summary';
import SurveyName from './SurveyName';
import SurveyParticipants from './SurveyParticipants';
import Template from './Template';
import Validation from './Validation';

/**
 * @deprecated 09-01-2025 confirm before deletion: https://peoplemesh.slack.com/archives/C01AYG5JEAF/p1736420210454079
 */
const PulseKickstart = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const [step, setStep] = useState(match.params.step || PULSE_KICKSTART_STEPS[0]);
  const [maxStepConvered, setMaxStepCovered] = useState(null);
  const [formId, setFormId] = useState(match.params.formId);
  const [currentStage, setCurrentStage] = useState('');
  const [isDraft, setIsDraft] = useState(true);
  const [isPublished, setIsPublished] = useState(false);

  const getForm = () => {
    if (!formId || Number.isNaN(Number(formId))) {
      return;
    }
    getSurvey({ surveyId: formId }).then((res) => {
      if (res) {
        setCurrentStage(res.currentStage);
        setIsDraft(res.currentStage === 'DRAFT');
        setIsPublished(res.currentStage === 'PUBLISHED');
        setMaxStepCovered(res.lastEnabledSection);
      }
    });
  };

  useEffect(getForm, [formId]);

  const goBack = () => {
    const currentStepIndex = PULSE_KICKSTART_STEPS.indexOf(step);
    if (currentStepIndex === -1) {
      console.error(`Invalid step found: ${step}`);
      return;
    }
    if (step === PULSE_KICKSTART_STEPS[0]) {
      goToPulseSettingPage(history);
      return;
    }
    const newStep = PULSE_KICKSTART_STEPS[currentStepIndex - 1];
    updateMaxStep(newStep);
    setStep(newStep);
    if (checkNewNavEnabled()) {
      const routeToGo = getDeerPulseKickstartRoute(formId, newStep);
      history.push(routeToGo);
    } else {
      goToPulseKickstart({ history, entityId: formId, step: newStep });
    }
  };

  const goToNextStep = () => {
    const currentStepIndex = PULSE_KICKSTART_STEPS.indexOf(step);
    if (currentStepIndex === -1) {
      console.error(`Invalid step found: ${step}`);
      return;
    }
    if (step === PULSE_KICKSTART_STEPS[PULSE_KICKSTART_STEPS.length - 1]) {
      goToPulseSettingPage(history);
      return;
    }
    const newStep = PULSE_KICKSTART_STEPS[currentStepIndex + 1];
    setStep(newStep);
    if (step !== PULSE_KICKSTART_STEPS[0]) {
      updateMaxStep(newStep);
      if (checkNewNavEnabled()) {
        const routeToGo = getDeerPulseKickstartRoute(formId, newStep);
        history.push(routeToGo);
      } else {
        goToPulseKickstart({ history, entityId: formId, step: newStep });
      }
    }
  };

  const setCustomStep = (value) => {
    updateMaxStep(value);
    setStep(value);
    if (checkNewNavEnabled()) {
      const routeToGo = getDeerPulseKickstartRoute(formId, value);
      history.push(routeToGo);
    } else {
      goToPulseKickstart({ history, entityId: formId, step: value });
    }
  };

  const updateMaxStep = (step) => {
    const currentStepIndex = PULSE_KICKSTART_STEPS.indexOf(step);
    const maxStepIndex = PULSE_KICKSTART_STEPS.indexOf(maxStepConvered);
    if (currentStepIndex > maxStepIndex) {
      setMaxStepCovered(step);
    }
  };

  const updateFormId = (entityId) => {
    setFormId(entityId);
    if (checkNewNavEnabled()) {
      const routeToGo = getDeerPulseKickstartRoute(formId, PULSE_KICKSTART_STEPS[1]);
      history.push(routeToGo);
    } else {
      goToPulseKickstart({ history, entityId, step: PULSE_KICKSTART_STEPS[1] });
    }
  };

  return (
    <CohortCreationSidePanelControlProvider>
      <Box display="flex">
        <PulseKickstartSteps
          formId={formId}
          currentStep={step}
          navigateToStep={setCustomStep}
          maxStepConvered={maxStepConvered}
          currentStage={currentStage}
          isDraft={isDraft}
        />
        <RightPanel>
          {step === PULSE_KICKSTART_STEPS[0] && (
            <SurveyName updateFormId={updateFormId} formId={formId} backFn={isDraft && goBack} nextFn={goToNextStep} />
          )}
          {step === PULSE_KICKSTART_STEPS[1] && (
            <SurveyParticipants formId={formId} backFn={isDraft && goBack} nextFn={goToNextStep} />
          )}
          {step === PULSE_KICKSTART_STEPS[2] && (
            <Template formId={formId} backFn={isDraft && goBack} nextFn={goToNextStep} />
          )}
          {step === PULSE_KICKSTART_STEPS[3] && (
            <Settings formId={formId} backFn={isDraft && goBack} nextFn={goToNextStep} isDraft={isDraft} />
          )}
          {step === PULSE_KICKSTART_STEPS[4] && (
            <Reminders formId={formId} backFn={isDraft && goBack} nextFn={goToNextStep} />
          )}
          {step === PULSE_KICKSTART_STEPS[5] && (
            <Validation formId={formId} backFn={isDraft && goBack} nextFn={goToNextStep} />
          )}
          {step === PULSE_KICKSTART_STEPS[6] && (
            <Summary formId={formId} backFn={!isPublished && goBack} nextFn={goToNextStep} isDraft={isDraft} />
          )}
        </RightPanel>
        <CohortCreationContextProvider>
          <CohortSidepanel defaultCohortVisibility="ADMIN" requiredCohortName generalCohortCreation />
        </CohortCreationContextProvider>
      </Box>
    </CohortCreationSidePanelControlProvider>
  );
};

const RightPanel = styled.div`
  width: 100%;
  padding: 2.5em 2.25em;
  ${() =>
    checkNewNavEnabled() &&
    css`
      width: calc(100vw - 367px);
      position: fixed;
      left: 367px;
      top: 0;
      bottom: 0;
      right: 0;
      overflow-y: auto;
      padding: 16px 12px;
    `}
`;

/**
 * @deprecated 09-01-2025 confirm before deletion: https://peoplemesh.slack.com/archives/C01AYG5JEAF/p1736420210454079
 */
export default withRouter(PulseKickstart);
